import JobCard from "@/components/Shared/JobCard";
import FooterWave2Icon from "@/public/site-assets/svg/footer-wave-2.svg";
import Arrow from "@/public/site-assets/svg/swiper-arrow.svg";
import clsx from "classnames";
import { Container } from "reactstrap";
import Buttons from "ui/Buttons/component";
import styles from "./styles.module.scss";
import LaptopIcon from "/public/site-assets/svg/laptop.svg";
import LightBlulbIcon from "/public/site-assets/svg/light-blub.svg";
import StarIcon from "/public/site-assets/svg/star.svg";

import { motion } from "framer-motion";
import { useEffect, useRef, useState } from "react";

import useJobs from "hooks/useJobs";
import { Autoplay, Navigation } from "swiper";
import "swiper/css";
import "swiper/css/autoplay";
import { Swiper, SwiperSlide } from "swiper/react";

export const LatestJobs = ({
  className,
  heading,
  with_swiper,
  buttons,
  swiperID = "latest-jobs",
  xl = 5,
  lg = 4,
  md = 3,
  sm = 2,
  showWave,
  waveDirection,
  waveColor,
  waveBackground,
  showTopWave,
  topWaveDirection,
  topWaveColor,
  topWaveBackground,
}) => {
  const post = useJobs();

  const sectionRef = useRef(null);
  const [isVisible, setIsVisible] = useState(false);
  const [visiblePosts, setVisiblePosts] = useState(9);

  const loadMore = () => {
    setVisiblePosts((prev) => prev + 9);
  };

  const settings = {
    modules: [Navigation, Autoplay],
    loop: post?.length >= 4 ? true : false,
    spaceBetween: 40,
    centeredSlides: false,
    slidesPerView: 1,
    autoHeight: false,
    speed: 500,
    autoplay: {
      delay: 5000,
    },
    navigation: {
      nextEl: `.${swiperID}.swiper-next`,
      prevEl: `.${swiperID}.swiper-prev`,
    },
    breakpoints: {
      320: {
        spaceBetween: 10,
        slidesPerView: sm,
      },
      768: {
        spaceBetween: 40,
        slidesPerView: md,
      },
      991: {
        spaceBetween: 40,
        slidesPerView: lg,
      },
      1399: {
        spaceBetween: 40,
        slidesPerView: xl,
      },
    },
  };

  useEffect(() => {
    const options = {
      root: null,
      rootMargin: "0px",
      threshold: 0.1,
    };

    const observer = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
          observer.unobserve(entry.target);
        }
      });
    }, options);

    if (sectionRef.current) {
      observer.observe(sectionRef.current);
    }

    return () => {
      observer.disconnect();
    };
  }, []);

  useEffect(() => {
    const handleResize = () => {
      const mobileDevice = window.innerWidth <= 991;
    };

    handleResize();
    window.addEventListener("resize", handleResize);

    return () => window.removeEventListener("resize", handleResize);
  }, []);

  // Must stay below every hook call so hook order is identical on each render
  if (!post?.length) {
    return null;
  }

  return (
    <>
      {showTopWave && (
        <div
          className={clsx(
            "footer",
            topWaveDirection === "down" && "rotate",
            topWaveColor,
            topWaveBackground,
          )}
          style={{ marginBottom: "-1px", zIndex: 1, position: "relative" }}
        >
          <FooterWave2Icon />
        </div>
      )}
      <section
        ref={sectionRef}
        className={clsx(
          className,
          styles.root,
          showTopWave ? "pt-0" : "pt-5",
          showWave ? "pb-0" : "pb-5",
          "position-relative",
        )}
      >
        <Container className="position-relative">
          <div className="floating-icon">
            <LightBlulbIcon />
          </div>
          <div className="floating-icon d-none d-lg-block">
            <StarIcon />
          </div>
          <div className="floating-icon">
            <LaptopIcon />
          </div>
          <div className="wrapper position-relative">
            {with_swiper ? (
              <div className="d-flex justify-content-between gap-4 align-items-center mb-5">
                <div>{heading && <h3>{heading}</h3>}</div>
                <div className="d-flex align-items-center">
                  <button
                    className={`${swiperID} swiper-prev border-0 bg-transparent`}
                    aria-label="Prev"
                  >
                    <Arrow />
                  </button>
                  <button
                    className={`${swiperID} swiper-next border-0 bg-transparent`}
                    aria-label="Next"
                  >
                    <Arrow />
                  </button>
                </div>
              </div>
            ) : (
              <div>{heading && <h3 className="h3 text-center mb-5">{heading}</h3>}</div>
            )}
            {with_swiper ? (
              post?.length >= 1 ? (
                <div className="all-jobs-wrapper with-swiper">
                  <Swiper {...settings}>
                    {post?.map((post, index) => (
                      <SwiperSlide key={index}>
                        <motion.div
                          className={`h-100 pb-3`}
                          initial={{ opacity: 0, y: 50 }}
                          animate={isVisible ? { opacity: 1, y: 0 } : {}}
                          transition={{ duration: 0.5, delay: index * 0.2 }}
                        >
                          <JobCard post={post} />
                        </motion.div>
                      </SwiperSlide>
                    ))}
                  </Swiper>
                </div>
              ) : (
                <div className="text-center">There's no available jobs at the moment.</div>
              )
            ) : (
              <>
                <div className="all-jobs-wrapper">
                  {post?.length >= 1 &&
                    post.slice(0, visiblePosts).map((post, index) => (
                      <div key={index} className="column">
                        <JobCard post={post} />
                      </div>
                    ))}
                </div>
                {post?.length > visiblePosts && (
                  <div className="text-center mt-5 pt-4">
                    <button onClick={loadMore} className="primaryBtn">
                      Load More
                    </button>
                  </div>
                )}
              </>
            )}

            {buttons && (
              <Buttons buttons={buttons} className="d-flex align-items-center text-center" />
            )}
          </div>
        </Container>
      </section>
      {showWave && (
        <div
          className={clsx(
            "footer",
            waveDirection === "down" && "rotate",
            waveColor,
            waveBackground,
          )}
          style={{ marginTop: "-1px" }}
        >
          <FooterWave2Icon />
        </div>
      )}
    </>
  );
};

export default LatestJobs;
